import { useState } from 'react'
import PropTypes from 'prop-types'
import { useTheme } from '@emotion/react'

import { PlusIcon, MinusIcon } from '../../../../components'
import { percentLabel } from '../../../../utils/utils'
import { DoughnutChart } from '../../../shared'
import {
  AccordionWrap,
  AccordionTrigger,
  AccordionQuestion,
  IconBox,
  AccordionBody,
  BalanceBlock,
  BalanceName,
  BalanceAmount,
  Caption,
  PieRow,
  PieColumn,
  ValueColumnHeading,
  PieValue,
} from './style'

const QUESTION = 'How much of my total account balance does my company stock represent?'

// One pie column: a repeated CURRENT/Your changes (or Recommended) header, the
// doughnut with the percentage inside, the dollar value, and the "XX% of your
// account" caption. The header is shown on every breakpoint — on mobile the
// data table drops its column headers, so these are the only cue for which
// donut is which.
const PieColumnView = ({ slice, planned, header, height }) => {
  const theme = useTheme()
  return (
    <PieColumn>
      <ValueColumnHeading planned={planned}>{header}</ValueColumnHeading>
      <DoughnutChart
        percent={slice.percent}
        height={height}
        isOld={!planned}
        labelColor={planned ? theme.primaryColor : theme.lightestGray}
        labelWeight={planned ? 700 : 400}
      />
      <PieValue planned={planned}>{slice.dollar}</PieValue>
      <Caption pie>{percentLabel(slice.percent)}% of your account</Caption>
    </PieColumn>
  )
}

/**
 * The account-balance lock-up accordion, collapsed by default. Expands to show
 * the employer-plan account nickname and total balance, plus the current-vs-
 * recommended company-stock concentration pie charts. Per the user story it uses
 * the plus/minus expand/collapse icons.
 */
const BalanceAccordion = ({ accountName, totalBalance, pie, isModified, isMobile }) => {
  const [expanded, setExpanded] = useState(false)
  const rightHeader = isModified ? 'Your changes' : 'Recommended'
  const pieHeight = isMobile ? 115 : 144

  return (
    <AccordionWrap>
      <AccordionTrigger
        type='button'
        aria-expanded={expanded}
        onClick={() => setExpanded(prev => !prev)}>
        <IconBox>{expanded ? <MinusIcon /> : <PlusIcon />}</IconBox>
        <AccordionQuestion>{QUESTION}</AccordionQuestion>
      </AccordionTrigger>

      {expanded && (
        <AccordionBody>
          <BalanceBlock>
            <BalanceName>{accountName}</BalanceName>
            <BalanceAmount>{totalBalance}</BalanceAmount>
            <Caption>Total balance</Caption>
          </BalanceBlock>

          <PieRow>
            <PieColumnView slice={pie.current} header='Current' height={pieHeight} />
            <PieColumnView
              slice={pie.recommended}
              planned
              header={rightHeader}
              height={pieHeight}
            />
          </PieRow>
        </AccordionBody>
      )}
    </AccordionWrap>
  )
}

const sliceShape = PropTypes.shape({
  percent: PropTypes.number,
  dollar: PropTypes.string,
})

BalanceAccordion.propTypes = {
  accountName: PropTypes.string,
  totalBalance: PropTypes.string,
  pie: PropTypes.shape({ current: sliceShape, recommended: sliceShape }).isRequired,
  isModified: PropTypes.bool,
  isMobile: PropTypes.bool,
}

export default BalanceAccordion
